import { MarketConquestFilterType } from '../../enums/MarketConquestFilterType';
import { FilterItemResource, MinMaxFilter } from '../../types/FilterTypes';
import { FilterType, SelectedFilterResource } from '../../types/MarketConquestFilterResource';
import { MarketConquestRequestResource } from '../../types/MarketConquestRequestResource';
import { OpportunityMapResource } from '../../types/OpportunityMapResource';
import { defaultStatus, initialLastUpdated } from '../../types/PortfolioFilterResource';
import { MarketConquestActions, MarketConquestActionsType } from './MarketConquestActions';

export type MapState = {
  isError: boolean;
  isLoading: boolean;
  config: google.maps.MapOptions;
  data: OpportunityMapResource;
  selectedDealer: string;
  dropdownSelectedZips: FilterItemResource[];
  selectedAudience: string;
};

export type MarketConquestState = {
  filters: FilterType;
  selectedFilters: SelectedFilterResource;
  isFilterChanged: boolean;
  masterFilterKey: MarketConquestFilterType;
  defaultLastUpdated: MinMaxFilter<number>;
  marketConquestRequest: MarketConquestRequestResource;
  totalRecordsCount: number;
  shouldRefresh: boolean;
  isLoading: boolean;
  haveInventoryAcquisition?: boolean;
  isFiltersShowing: boolean;
  map: MapState;
};

export const initialMarketConquestState: MarketConquestState = {
  selectedFilters: {
    models: [],
    trims: [],
    years: [],
    makes: [],
    brands: [],
    lastUpdated: null,
    assignees: [],
    audiences: [],
    activities: [],
    status: defaultStatus,
    campaignExpiryDates: [],
  },
  filters: {
    assignees: [],
    audiences: [],
    brandsLikelyOwned: [],
    campaignActivities: [],
    maxDate: new Date(),
    minDate: new Date(),
    replacementModels: [],
    replacementTrims: [],
    replacementYears: [],
    replacementMakes: [],
    statuses: [],
    campaignExpiryDates: [],
  },
  isFilterChanged: false,
  masterFilterKey: null,
  defaultLastUpdated: initialLastUpdated,
  marketConquestRequest: null,
  totalRecordsCount: 0,
  shouldRefresh: false,
  isLoading: false,
  haveInventoryAcquisition: null,
  isFiltersShowing: false,
  map: {
    isError: false,
    isLoading: false,
    config: null,
    data: null,
    selectedDealer: null,
    dropdownSelectedZips: [],
    selectedAudience: '',
  },
};

export const marketConquestReducer = (
  state: MarketConquestState = initialMarketConquestState,
  action: MarketConquestActions,
): MarketConquestState => {
  switch (action.type) {
    case MarketConquestActionsType.SET_IS_FILTERS_CHANGED:
      return {
        ...state,
        isFilterChanged: action.payload,
      };

    case MarketConquestActionsType.SET_DEFAULT_LAST_UPDATED:
      return {
        ...state,
        defaultLastUpdated: action.payload,
      };

    case MarketConquestActionsType.SET_MASTER_FILTER:
      return {
        ...state,
        masterFilterKey: action.payload,
      };

    case MarketConquestActionsType.SET_DASHBOARD_FILTERS:
      return {
        ...state,
        filters: action.payload,
      };

    case MarketConquestActionsType.SET_SELECTED_FILTERS:
      return {
        ...state,
        selectedFilters: action.payload,
      };

    case MarketConquestActionsType.SET_MARKET_CONQUEST_REQUEST_RESOURCE: {
      return {
        ...state,
        marketConquestRequest: action.payload,
      };
    }

    case MarketConquestActionsType.SET_DASHBOARD_DATA_TOTAL_COUNT: {
      return {
        ...state,
        totalRecordsCount: action.payload,
      };
    }

    case MarketConquestActionsType.SET_SHOULD_REFRESH: {
      return {
        ...state,
        shouldRefresh: action.payload,
      };
    }

    case MarketConquestActionsType.SET_IS_LOADING: {
      return {
        ...state,
        isLoading: action.payload,
      };
    }

    case MarketConquestActionsType.SET_HAS_INVENTORY_ACQUISITION:
      return {
        ...state,
        haveInventoryAcquisition: action.payload,
      };

    case MarketConquestActionsType.SET_IS_FILTERS_SHOWING: {
      return {
        ...state,
        isFiltersShowing: action.payload,
      };
    }

    case MarketConquestActionsType.SET_IS_MAP_LOADING: {
      return {
        ...state,
        map: {
          ...state.map,
          isLoading: action.payload,
        },
      };
    }

    case MarketConquestActionsType.SET_IS_MAP_ERROR: {
      return {
        ...state,
        map: {
          ...state.map,
          isError: action.payload,
        },
      };
    }

    case MarketConquestActionsType.SET_DROPDOWN_SELECTED_ZIPS: {
      return {
        ...state,
        map: {
          ...state.map,
          dropdownSelectedZips: action.payload,
        },
      };
    }

    case MarketConquestActionsType.SET_MAP_CONFIG: {
      return {
        ...state,
        map: {
          ...state.map,
          config: action.payload,
        },
      };
    }

    case MarketConquestActionsType.SET_MAP_DATA: {
      return {
        ...state,
        map: {
          ...state.map,
          data: action.payload,
        },
      };
    }

    case MarketConquestActionsType.SET_SELECTED_DEALER: {
      return {
        ...state,
        map: {
          ...state.map,
          selectedDealer: action.payload,
        },
      };
    }

    case MarketConquestActionsType.SET_DROPDOWN_SELECTED_AUDIENCE: {
      return {
        ...state,
        map: {
          ...state.map,
          selectedAudience: action.payload,
        },
      };
    }

    default:
      return state;
  }
};
